import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { COMMON_CURRENCIES } from "@/lib/masterOrderApi";
import { ChevronDown, Search } from "lucide-react";

/* Extensible ISO-currency picker. Backend accepts any 3-letter ISO code.
 * UI shows common list + free-text search that also lets typing any 3-letter code. */
export default function CurrencyField({ value, onChange, testid = "mo-currency" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toUpperCase();
    if (!q) return COMMON_CURRENCIES;
    return COMMON_CURRENCIES.filter(
      (c) => c.code.startsWith(q) || c.label.toUpperCase().includes(q)
    );
  }, [query]);

  const commit = (code) => {
    const clean = String(code || "").trim().toUpperCase();
    if (clean.length === 3 && /^[A-Z]{3}$/.test(clean)) {
      onChange(clean);
      setQuery("");
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-10 px-3 rounded-lg text-[13px] flex items-center justify-between"
          style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
          data-testid={testid}
        >
          <span>{value || <span className="text-[#B9A692]">ISO-код (напр. EUR)</span>}</span>
          <ChevronDown size={14} strokeWidth={1.8} color="#8B6F4B" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[280px]">
        <div className="p-2 border-b" style={{ borderColor: "#EFE4D0" }}>
          <div className="flex items-center gap-2 h-9 px-3 rounded-lg"
               style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
            <Search size={13} strokeWidth={1.7} color="#8B6F4B" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.length >= 3) commit(query);
              }}
              placeholder="EUR, USD, TRY, KZT..."
              className="flex-1 bg-transparent outline-none text-[13px]"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="text-[10.5px] mt-1.5 px-1" style={{ color: "#8B6F4B" }}>
            Введите любой 3-буквенный ISO-код и нажмите Enter, либо выберите из списка.
          </div>
        </div>
        <div className="max-h-56 overflow-y-auto py-1">
          {filtered.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => commit(c.code)}
              className="w-full flex items-center gap-3 px-3 py-1.5 text-[13px] text-left hover:bg-black/5"
              style={{ color: "#2A1D14" }}
              data-testid={`${testid}-opt-${c.code}`}
            >
              <span className="font-medium tabular-nums w-10">{c.code}</span>
              <span className="opacity-75 truncate">{c.label}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-3 text-[12px]" style={{ color: "#8B6F4B" }}>
              Кода нет в подборке — введите ISO-код руками и нажмите Enter.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
